"use client";

import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { components } from "@ala/codegen/account-service-api-schema.ts";
import { currentConfig } from "@ala/core/constants";
import { usePathname } from "@ala/core/navigation";

import { useTenantCookie } from "./use-tenant-cookie";
import { useUser } from "./use-user";

type Tenant = components["schemas"]["TenantResponse"];

interface TenantContextValue {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  isTenantLoading: boolean;
  switchTenant: (tenant: Tenant) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children?: ReactNode;
  onTenantSwitch: (tenantId: string) => void;
}

export const TenantProvider = ({ children, onTenantSwitch }: TenantProviderProps) => {
  const [isTenantLoading, setIsTenantLoading] = useState(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const pathname = usePathname();

  const { tenantCookie, updateTenantCookie } = useTenantCookie();

  const user = useUser();

  const updateTenantValues = useCallback(
    (tenant: Tenant) => {
      setCurrentTenant(tenant);
      updateTenantCookie(tenant.id);
    },
    [updateTenantCookie],
  );

  const switchTenant = useCallback(
    async (tenant: Tenant) => {
      if (!tenants.includes(tenant)) {
        return;
      }

      updateTenantValues(tenant);
      onTenantSwitch(tenant.id);
    },
    [tenants, updateTenantValues, onTenantSwitch],
  );

  useEffect(() => {
    if (pathname === "/onboarding") {
      return;
    }

    if (user?.isLoading) return;
    setIsTenantLoading(false);

    if (!user?.data) return;
    setTenants(user.data.tenants);

    const isOnChatSubdomain = window.location.hostname.startsWith("chat.");
    const isOnLandingPage = !isOnChatSubdomain && pathname === "/";
    const shouldRedirectToOnboarding = user.data.tenants.length === 0 && !isOnLandingPage;

    if (shouldRedirectToOnboarding) {
      window.location.href = `${currentConfig.appUrl}/onboarding`;
      return;
    }

    const activeTenants = user.data.tenants.filter((tenant) => tenant.joined);

    if (tenantCookie) {
      const tenant = activeTenants.find((tenant) => tenant.id === tenantCookie);

      if (tenant) {
        updateTenantValues(tenant);
        return;
      }
    }

    const firstTenant: Tenant | undefined = activeTenants?.[0];

    if (firstTenant) {
      updateTenantValues(firstTenant);
    }
  }, [user?.data, user?.isLoading, tenantCookie, pathname, updateTenantValues]);

  const value = useMemo(
    () => ({ tenants, currentTenant, isTenantLoading, switchTenant }),
    [tenants, currentTenant, isTenantLoading, switchTenant],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenantContext must be used within a TenantProvider");
  }

  return context;
};
